// src/components/DonorNotifications.js
import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp,
  getDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { sendRequestStatusEmails } from "../services/emailNotifications";
import {
  Alert,
  Spinner,
  Card,
  ListGroup,
  Badge,
  Button,
  Collapse,
  Row,
  Col,
} from "react-bootstrap";

/* =========================
   Constantes / helpers
========================= */
const ES = {
  PENDIENTE: "pendiente",
  EN_REVISION: "en revisión",
  APROBADA: "aprobada",
  RECHAZADA: "rechazada",
  COMPLETADA: "completada",
};

const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

const toSpanishCanonical = (status) => {
  const s = normalize(status);
  if (["pending", "pendiente"].includes(s)) return ES.PENDIENTE;
  if (
    ["under_review", "en revisión", "en revision", "bajo revisión", "bajo revision"].includes(s)
  )
    return ES.EN_REVISION;
  if (["approved", "aprobada", "aprobado", "accept", "accepted"].includes(s))
    return ES.APROBADA;
  if (["rejected", "rechazada", "rechazado"].includes(s)) return ES.RECHAZADA;
  if (["completed", "completada", "completado"].includes(s)) return ES.COMPLETADA;
  return s || ES.PENDIENTE;
};

const isActionable = (status) => {
  const s = toSpanishCanonical(status);
  return s === ES.PENDIENTE || s === ES.EN_REVISION;
};

const isImageUrl = (url) => /\.(png|jpe?g|webp)(\?.*)?$/i.test(url || "");

const getStatusBadge = (status) => {
  const s = toSpanishCanonical(status);
  if (s === ES.PENDIENTE) return "warning";
  if (s === ES.EN_REVISION) return "info";
  if (s === ES.APROBADA) return "success";
  if (s === ES.RECHAZADA) return "danger";
  if (s === ES.COMPLETADA) return "primary";
  return "secondary";
};

const pretty = (status) => {
  const s = toSpanishCanonical(status);
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatDate = (val) => {
  try {
    const d = val?.toDate ? val.toDate() : new Date(val);
    if (Number.isNaN(d?.getTime?.())) return "Fecha no disponible";
    return d.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Fecha no disponible";
  }
};

// util para batches de 10 en where("in", [...])
const chunk = (arr, n = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

/* =========================
   Componente
========================= */
const DonorNotifications = () => {
  const [user] = useAuthState(auth);
  const [role, setRole] = useState(null); // "superadmin" | otro | null (no cargado)

  const [requests, setRequests] = useState([]);
  const [actionable, setActionable] = useState([]);
  const [otherRequests, setOtherRequests] = useState([]);

  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [showCompleted, setShowCompleted] = useState(false);
  const [updating, setUpdating] = useState({}); // { [requestId]: true }

  /* ===== 1) Cargar rol del usuario ===== */
  useEffect(() => {
    const getRole = async () => {
      try {
        if (!user?.uid) {
          setRole(null);
          return;
        }
        const usnap = await getDoc(doc(db, "users", user.uid));
        setRole(usnap.exists() ? usnap.data().role || null : null);
      } catch {
        setRole(null);
      }
    };
    getRole();
  }, [user?.uid]);

  /* ===== 2) Cargar solicitudes ===== */
  useEffect(() => {
    const fetchRequests = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      setLoading(true);

      const safeGetDocs = async (q) => {
        try {
          const res = await getDocs(q);
          return res.docs;
        } catch (e) {
          if (e.code === "permission-denied") {
            console.warn("Consulta omitida por permisos.");
            return [];
          }
          throw e;
        }
      };

      try {
        const collected = new Map();

        if (role === "superadmin") {
          // SUPERADMIN: ve todo
          const qAll = query(
            collection(db, "requests"),
            orderBy("createdAt", "desc"),
            limit(500)
          );
          const all = await safeGetDocs(qAll);
          all.forEach((d) => collected.set(d.id, { id: d.id, ...d.data() }));
        } else {
          // DONANTE: múltiples vías de vinculación
          const byDonorId = await safeGetDocs(
            query(collection(db, "requests"), where("donorId", "==", user.uid))
          );
          const byLegacy = await safeGetDocs(
            query(
              collection(db, "requests"),
              where("medicationdonorID", "==", user.uid)
            )
          );
          const byEmail =
            user.email
              ? await safeGetDocs(
                  query(
                    collection(db, "requests"),
                    where("donorEmail", "==", user.email)
                  )
                )
              : [];

          // 4) Si el request no tiene vínculos directos: buscar por medicamentos del donante
          const medsByOwner = await safeGetDocs(
            query(collection(db, "medications"), where("donorId", "==", user.uid))
          );
          const medsByEmail =
            user.email
              ? await safeGetDocs(
                  query(
                    collection(db, "medications"),
                    where("donorEmail", "==", user.email)
                  )
                )
              : [];
          const myMedIds = [...new Set([...medsByOwner, ...medsByEmail].map((d) => d.id))];

          let byMedId = [];
          for (const part of chunk(myMedIds, 10)) {
            const qIn = query(
              collection(db, "requests"),
              where("medicationId", "in", part)
            );
            const rows = await safeGetDocs(qIn);
            byMedId = byMedId.concat(rows);
          }

          [byDonorId, byLegacy, byEmail, byMedId].flat().forEach((d) => {
            if (!collected.has(d.id)) collected.set(d.id, { id: d.id, ...d.data() });
          });
        }

        // Enriquecer con datos del medicamento + normalizar estado
        const base = Array.from(collected.values());

        // Ordenar por createdAt desc para consistencia (porque juntamos varias consultas)
        base.sort((a, b) => {
          const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const dbb = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return dbb - da;
        });

        const enriched = await Promise.all(
          base.map(async (r) => {
            let med = null;
            if (r.medicationId) {
              const msnap = await getDoc(doc(db, "medications", r.medicationId));
              if (msnap.exists()) med = { id: msnap.id, ...msnap.data() };
            }
            return {
              ...r,
              status: toSpanishCanonical(r.status),
              _med: {
                name: r.medicationName ?? med?.name ?? "No especificado",
                category: r.medicationCategory ?? med?.category ?? "No especificada",
                dosage: r.medicationDosage ?? med?.dosage ?? "No especificada",
                condition: r.medicationCondition ?? med?.condition ?? "No especificada",
                quantity:
                  r.medicationQuantity ??
                  (typeof med?.quantity !== "undefined"
                    ? String(med.quantity)
                    : "No especificada"),
                expirationDate:
                  r.medicationExpirationDate ?? med?.expirationDate ?? "No especificada",
                description: r.medicationDescription ?? med?.description ?? "",
                imageUrl: r.medicationImageUrl ?? med?.imageUrl ?? null,
              },
            };
          })
        );

        setRequests(enriched);
        setActionable(enriched.filter((r) => isActionable(r.status)));
        setOtherRequests(enriched.filter((r) => !isActionable(r.status)));
      } catch (error) {
        console.error("Error al cargar solicitudes:", error);
        setAlert({
          show: true,
          message: "Error al cargar las solicitudes. Intente nuevamente más tarde.",
          variant: "danger",
        });
      } finally {
        setLoading(false);
      }
    };

    if (role !== null) fetchRequests();
  }, [user, role]);

  /* ===== 3) Acciones ===== */
  const sendNotification = async (request, spanishStatus) => {
    try {
      await addDoc(collection(db, "notifications"), {
        userId: request.userId,
        requestId: request.id,
        type: "request_update",
        message: `Tu solicitud de ${request.medicationName} ha sido ${spanishStatus.toUpperCase()}`,
        status: spanishStatus, // guardamos en español
        read: false,
        createdAt: serverTimestamp(),
        donorName: request.donorName || user?.displayName || "Un donante",
        medicationName: request.medicationName,
        medicationId: request.medicationId,
        quantity: request.quantity || 1,
      });
    } catch (e) {
      console.error("Error enviando notificación:", e);
    }
  };

  const handleStatusChange = async (requestId, nextStatusES) => {
    const normalized = toSpanishCanonical(nextStatusES); // "aprobada" / "rechazada"
    try {
      setUpdating((p) => ({ ...p, [requestId]: true }));
      await updateDoc(doc(db, "requests", requestId), {
        status: normalized,
        updatedAt: serverTimestamp(),
      });

      const full = requests.find((r) => r.id === requestId);
      if (full) {
        await sendNotification(full, normalized);
        await sendRequestStatusEmails({
          requestId,
          medicationName: full.medicationName,
          status: normalized,
          requesterEmail: full.userEmail,
          donorEmail: full.donorEmail || user?.email,
          actorRole: role === "superadmin" ? "superadmin" : "donante",
        });
      }

      // Refrescar local
      const updatedAll = requests.map((r) =>
        r.id === requestId ? { ...r, status: normalized } : r
      );
      setRequests(updatedAll);
      setActionable(updatedAll.filter((r) => isActionable(r.status)));
      setOtherRequests(updatedAll.filter((r) => !isActionable(r.status)));

      setAlert({
        show: true,
        message: `Solicitud ${
          normalized === ES.APROBADA ? "aprobada" : "rechazada"
        } correctamente`,
        variant: normalized === ES.APROBADA ? "success" : "info",
      });
    } catch (e) {
      console.error("Error al actualizar estado:", e);
      setAlert({
        show: true,
        message: "Error al actualizar la solicitud: " + e.message,
        variant: "danger",
      });
    } finally {
      setUpdating((p) => ({ ...p, [requestId]: false }));
    }
  };

  /* ===== 4) Render helpers ===== */
  const renderPrescription = (req) => {
    if (!req.prescriptionUrl) return <span className="text-muted">No subida aún</span>;
    return isImageUrl(req.prescriptionUrl) ? (
      <div className="mt-2">
        <img
          src={req.prescriptionUrl}
          alt={req.prescriptionName || "Receta médica"}
          className="img-fluid img-thumbnail"
          style={{ maxHeight: 220, objectFit: "contain" }}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
        <div className="mt-2">
          <Button
            as="a"
            href={req.prescriptionUrl}
            target="_blank"
            rel="noreferrer"
            variant="outline-primary"
            size="sm"
          >
            Abrir receta en pestaña
          </Button>
        </div>
      </div>
    ) : (
      <Button
        as="a"
        href={req.prescriptionUrl}
        target="_blank"
        rel="noreferrer"
        variant="outline-primary"
        size="sm"
      >
        {req.prescriptionName || "Ver receta"}
      </Button>
    );
  };

  const renderRequestItem = (request) => {
    const statusES = toSpanishCanonical(request.status);
    const med = request._med || {};
    return (
      <ListGroup.Item key={request.id} className="mb-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="w-100">
            <Row>
              {med.imageUrl && (
                <Col md={3} className="mb-3 mb-md-0 text-center">
                  <img
                    src={med.imageUrl}
                    alt={`Imagen de ${med.name}`}
                    className="img-fluid img-thumbnail"
                    style={{ maxHeight: 200, objectFit: "contain" }}
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </Col>
              )}
              <Col md={med.imageUrl ? 9 : 12}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="mb-2">{med.name}</h5>
                    <Row>
                      <Col md={6}>
                        <p className="mb-1">
                          <strong>Categoría:</strong> {med.category}
                        </p>
                        <p className="mb-1">
                          <strong>Dosis:</strong> {med.dosage}
                        </p>
                        <p className="mb-1">
                          <strong>Condición:</strong> {med.condition}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-1">
                          <strong>Cantidad:</strong> {med.quantity}
                        </p>
                        <p className="mb-1">
                          <strong>Vencimiento:</strong> {med.expirationDate}
                        </p>
                        <p className="mb-1">
                          <strong>Fecha solicitud:</strong>{" "}
                          {formatDate(request.createdAt)}
                        </p>
                      </Col>
                    </Row>
                    {med.description && (
                      <p className="mt-2">
                        <strong>Descripción:</strong> {med.description}
                      </p>
                    )}
                    <div className="mt-3">
                      <p className="mb-1">
                        <strong>Solicitante:</strong>{" "}
                        {request.userName || request.userEmail}
                      </p>
                      {request.requesterNotes && (
                        <p className="mb-1">
                          <strong>Notas del solicitante:</strong>{" "}
                          {request.requesterNotes}
                        </p>
                      )}
                      <p className="mb-1">
                        <strong>Receta médica:</strong>
                      </p>
                      {renderPrescription(request)}
                    </div>

                    {isActionable(statusES) && (
                      <div className="d-flex gap-2 mt-3">
                        <Button
                          variant="success"
                          size="sm"
                          disabled={!!updating[request.id]}
                          onClick={() => handleStatusChange(request.id, ES.APROBADA)}
                        >
                          {updating[request.id] ? "Guardando…" : "Aprobar"}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={!!updating[request.id]}
                          onClick={() => handleStatusChange(request.id, ES.RECHAZADA)}
                        >
                          {updating[request.id] ? "Guardando…" : "Rechazar"}
                        </Button>
                      </div>
                    )}
                  </div>

                  <Badge bg={getStatusBadge(statusES)} className="fs-6 ms-2">
                    {pretty(statusES)}
                  </Badge>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </ListGroup.Item>
    );
  };

  /* ===== 5) UI ===== */
  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </div>
    );
  }

  const title =
    role === "superadmin" ? "Todas las Solicitudes" : "Solicitudes de Medicamentos";

  return (
    <div className="container mt-4">
      <h2 className="mb-4">{title}</h2>

      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() => setAlert((a) => ({ ...a, show: false }))}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      {/* Por aprobar */}
      {actionable.length === 0 ? (
        <Card className="mb-4">
          <Card.Body className="text-center">
            <Card.Text className="fs-5 text-muted">
              {role === "superadmin"
                ? "No hay solicitudes pendientes."
                : "No tienes solicitudes por aprobar"}
            </Card.Text>
          </Card.Body>
        </Card>
      ) : (
        <>
          <h4 className="mb-3">Por aprobar</h4>
          <ListGroup className="mb-4">{actionable.map(renderRequestItem)}</ListGroup>
        </>
      )}

      {/* Otras */}
      {otherRequests.length > 0 && (
        <>
          <Button
            variant="outline-secondary"
            onClick={() => setShowCompleted(!showCompleted)}
            className="mb-3"
          >
            {showCompleted ? "Ocultar" : "Mostrar"} otras solicitudes (
            {otherRequests.length})
          </Button>
          <Collapse in={showCompleted}>
            <div>
              <h4 className="mb-3">Otras Solicitudes</h4>
              <ListGroup>{otherRequests.map(renderRequestItem)}</ListGroup>
            </div>
          </Collapse>
        </>
      )}

      {/* Vacío total */}
      {actionable.length === 0 && otherRequests.length === 0 && (
        <Card className="mb-4">
          <Card.Body className="text-center text-muted">
            No se encontraron solicitudes.
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default DonorNotifications;
